import React, { useEffect } from 'react';
import styled from 'styled-components';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Navigation from './Navigation';
import { spacing } from '../design-system/tokens/spacing';
import { borders } from '../design-system/tokens/borders';
import { shadows } from '../design-system/tokens/shadows';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1200;
`;

const Drawer = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: min(280px, 85vw);
  background: ${props => props.theme.colors.surface};
  border-right: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${shadows.xl};
  z-index: 1300;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  border: none;
  background: none;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  border-radius: ${borders.radius.md};

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  closeLabel?: string;
}

export const MobileNavDrawer: React.FC<MobileNavDrawerProps> = ({
  open,
  onClose,
  ariaLabel,
  closeLabel,
}) => {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <Overlay onClick={onClose} aria-hidden />
      <Drawer id="mobile-nav-drawer" role="dialog" aria-modal="true" aria-label={ariaLabel}>
        <DrawerHeader>
          <CloseButton type="button" onClick={onClose} aria-label={closeLabel ?? ariaLabel}>
            <XMarkIcon width={24} height={24} />
          </CloseButton>
        </DrawerHeader>
        <Navigation onNavigate={onClose} />
      </Drawer>
    </>
  );
};

export default MobileNavDrawer;
